import React, { useState, useEffect } from "react";
import { Toaster, toast } from "react-hot-toast";
import { updateEmailFormat, fetchAllEmailTypes } from "../Services/APIService";
import {
  EmailFormat,
  EmailType,
  EmailFormatFormData,
} from "../types/emailFormat.types";
import QuillWrapper, {
  quillModules,
  quillFormats,
} from "../Components/QuillWrapper";

interface EditEmailFormatModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: EmailFormatFormData) => void;
  editEmailFormatData: EmailFormat | null;
}

interface ValidationErrors {
  typeId?: string;
  subject?: string;
  format?: string;
  attachment?: string;
}

interface SelectOption {
  value: number;
  label: string;
}

const EditEmailFormatModal: React.FC<EditEmailFormatModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  editEmailFormatData,
}) => {
  const [formData, setFormData] = useState<EmailFormatFormData>({
    typeId: 0,
    subject: "",
    format: "",
    attachment: "",
    isExcludeBuilderEmail: false,
    isExcludeLeadEmail: false,
    isExcludeUserEmail: false,
  });
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [emailTypes, setEmailTypes] = useState<EmailType[]>([]);
  const [isLoadingTypes, setIsLoadingTypes] = useState(true);
  const [errors, setErrors] = useState<ValidationErrors>({});

  const tenantId = Number(localStorage.getItem("tenantId")) || 1000;
  const userId = Number(localStorage.getItem("UserId") || 0);

  useEffect(() => {
    const fetchTypes = async () => {
      setIsLoadingTypes(true);
      try {
        const types = await fetchAllEmailTypes();
        setEmailTypes(types);
      } catch (error) {
        toast.error("Failed to load email types");
      } finally {
        setIsLoadingTypes(false);
      }
    };

    fetchTypes();
  }, []);

  useEffect(() => {
    if (editEmailFormatData && emailTypes.length > 0) {
      setFormData({
        typeId: editEmailFormatData.typeId,
        subject: editEmailFormatData.subject,
        format: editEmailFormatData.format,
        attachment: editEmailFormatData.attachment,
        isExcludeBuilderEmail: !!editEmailFormatData.isExcludeBuilderEmail,
        isExcludeLeadEmail: !!editEmailFormatData.isExcludeLeadEmail,
        isExcludeUserEmail: !!editEmailFormatData.isExcludeUserEmail,
      });
      setAttachmentFile(null);
    }
  }, [editEmailFormatData, emailTypes]);

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.split(",")[1] || "");
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};
    let isValid = true;

    if (!formData.typeId) {
      newErrors.typeId = "Email type is required";
      isValid = false;
    }
    if (!formData.subject.trim()) {
      newErrors.subject = "Subject is required";
      isValid = false;
    }
    if (!formData.format.trim() || formData.format === "<p><br></p>") {
      newErrors.format = "Format is required";
      isValid = false;
    }
    if (attachmentFile && attachmentFile.size > 5 * 1024 * 1024) {
      newErrors.attachment = "File size exceeds 5MB";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    if (value.startsWith(" ")) return;

    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));

    if (errors[name as keyof ValidationErrors]) {
      setErrors({ ...errors, [name]: undefined });
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setAttachmentFile(file);
      setErrors({ ...errors, attachment: undefined });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    let attachmentBase64 = formData.attachment;
    if (attachmentFile) {
      try {
        attachmentBase64 = await fileToBase64(attachmentFile);
      } catch (error) {
        toast.error("Failed to process attachment");
        return;
      }
    }

    const payload = {
      ...formData,
      attachment: attachmentBase64,
      id: editEmailFormatData?.id ?? 0,
      tenantId,
      modifiedBy: userId,
    };

    try {
      const result = await updateEmailFormat(payload);
      if (result) {
        onSubmit(formData);
        handleClose();
      } else {
        toast.error("Email format update failed");
      }
    } catch (error: any) {
      toast.error(error.message || "Update failed");
    }
  };

  const handleClose = () => {
    setFormData({
      typeId: 0,
      subject: "",
      format: "",
      attachment: "",
      isExcludeBuilderEmail: false,
      isExcludeLeadEmail: false,
      isExcludeUserEmail: false,
    });
    setAttachmentFile(null);
    setErrors({});
    onClose();
  };

  const requiredField = <span className="text-danger ms-1">*</span>;

  return (
    <>
      <Toaster position="top-center" />
      <div
        className={`modal-backdrop-custom ${isOpen ? "open" : "closed"}`}
        onClick={handleClose}
      ></div>
      <div className={`modal-custom ${isOpen ? "open" : "closed"}`}>
        <div className="modal-content">
          <div className="modal-header-custom">
            <h5 className="modal-title-custom">Edit Email Format</h5>
            <button className="btn-close-custom" onClick={handleClose}>
              <span className="icon-close"></span>
            </button>
          </div>
          <div className="modal-body-custom">
            <form onSubmit={handleSubmit}>
              <div className="row">
                {/* Email Type */}
                <div className="col-12 col-lg-12 mb-3 input-group-custom">
                  <label className="form-label" htmlFor="emailTypesDrp">
                    Email Type{requiredField}
                  </label>
                  <select
                    name="typeId"
                    value={formData.typeId || ""}
                    onChange={handleChange}
                    className={`form-select form-control-lg ${
                      errors.typeId ? "is-invalid" : ""
                    }`}
                  >
                    <option value="">Select Email Type</option>
                    {emailTypes
                      .filter((email) => email.id > 0)
                      .map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.typeName}
                        </option>
                      ))}
                  </select>
                  {errors.typeId && (
                    <div className="invalid-feedback text-danger">
                      {errors.typeId}
                    </div>
                  )}
                </div>

                {/* Subject */}
                <div className="col-12 col-lg-12 mb-3 input-group-custom">
                  <label className="form-label" htmlFor="subject">
                    Subject{requiredField}
                  </label>
                  <div className="input-with-icon">
                    <span className="input-icon icon-envelope"></span>
                    <input
                      name="subject"
                      id="subject"
                      placeholder="Enter Subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className={`form-control-custom1 ${
                        errors.subject ? "is-invalid" : ""
                      }`}
                    />
                    {errors.subject && (
                      <div className="invalid-feedback-custom">
                        {errors.subject}
                      </div>
                    )}
                  </div>
                </div>

                {/* Format */}
                <div className="col-12 col-lg-12 mb-3 input-group-custom">
                  <label className="form-label" htmlFor="format">
                    Format{requiredField}
                  </label>
                  <div className="input-with-icon">
                    <QuillWrapper
                      value={formData.format}
                      onChange={(content) => {
                        setFormData((prev) => ({ ...prev, format: content }));
                        if (
                          errors.format &&
                          content.trim() &&
                          content !== "<p><br></p>"
                        ) {
                          setErrors((prev) => ({ ...prev, format: undefined }));
                        }
                      }}
                      onBlur={() => {
                        if (
                          !formData.format.trim() ||
                          formData.format === "<p><br></p>"
                        ) {
                          setErrors((prev) => ({
                            ...prev,
                            format: "Format is required",
                          }));
                        }
                      }}
                      modules={quillModules}
                      formats={quillFormats}
                      className="quill-editor"
                    />
                    {errors.format && (
                      <div className="invalid-feedback-custom text-danger">
                        {errors.format}
                      </div>
                    )}
                  </div>
                </div>

                {/* Attachment */}
                <div className="col-12 mb-3">
                  <input
                    type="file"
                    onChange={handleFileChange}
                    className="form-control-custom1"
                    accept=".pdf,.doc,.docx,.jpg,.png"
                  />
                  {attachmentFile && (
                    <div className="text-muted">
                      Selected: {attachmentFile.name}
                    </div>
                  )}
                  {formData.attachment && !attachmentFile && (
                    <div className="text-success">
                      Attachment already uploaded
                    </div>
                  )}
                  {errors.attachment && (
                    <div className="text-danger">{errors.attachment}</div>
                  )}
                </div>

                {/* 🔹 New Checkboxes */}
                <div className="col-12 mb-2 input-group-custom">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="isExcludeBuilderEmail_edit"
                      name="isExcludeBuilderEmail"
                      checked={formData.isExcludeBuilderEmail}
                      onChange={handleCheckboxChange}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="isExcludeBuilderEmail_edit"
                    >
                      Exclude Builder Email
                    </label>
                  </div>
                </div>

                <div className="col-12 mb-2 input-group-custom">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="isExcludeLeadEmail_edit"
                      name="isExcludeLeadEmail"
                      checked={formData.isExcludeLeadEmail}
                      onChange={handleCheckboxChange}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="isExcludeLeadEmail_edit"
                    >
                      Exclude Lead Email
                    </label>
                  </div>
                </div>

                <div className="col-12 mb-3 input-group-custom">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="isExcludeUserEmail_edit"
                      name="isExcludeUserEmail"
                      checked={formData.isExcludeUserEmail}
                      onChange={handleCheckboxChange}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="isExcludeUserEmail_edit"
                    >
                      Exclude User Email
                    </label>
                  </div>
                </div>
              </div>
            </form>
          </div>
          <div className="modal-footer-custom">
            <button className="btn-custom btn-cancel" onClick={handleClose}>
              Cancel
            </button>
            <button
              className="btn-custom btn-primary-custom"
              onClick={handleSubmit}
            >
              Update
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditEmailFormatModal;
